import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { extractErrorMessage } from "@/shared/utils";
import { useAddCommentMutation, useEditCommentMutation, useDeleteCommentMutation } from "../api";
import { commentFormSchema, MAX_COMMENT_FILES, MAX_COMMENT_FILE_SIZE_MB, ALLOWED_COMMENT_MIME_TYPES } from "../validation";
import type { CommentFormValues } from "../validation";

interface Props {
  projectId: number;
  taskId: number;
  isTaskFetching: boolean;
}

interface PromptState {
  title: string;
  text: string;
  variant: "success" | "error";
}

export function useTaskComments({ projectId, taskId, isTaskFetching }: Props) {
  const { t } = useTranslation("TasksPage");
  const [addComment] = useAddCommentMutation();
  const [editComment] = useEditCommentMutation();
  const [deleteComment] = useDeleteCommentMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [waitingForRefetch, setWaitingForRefetch] = useState<"add" | "delete" | "edit" | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const prevFetchingRef = useRef(isTaskFetching);

  useEffect(() => {
    if (waitingForRefetch && prevFetchingRef.current && !isTaskFetching) {
      if (waitingForRefetch === "delete") {
        setDeletingId(null);
      }
      if (waitingForRefetch === "edit") {
        setEditingId(null);
        setIsEditSubmitting(false);
      }
      setWaitingForRefetch(null);
    }
    prevFetchingRef.current = isTaskFetching;
  }, [isTaskFetching, waitingForRefetch]);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<CommentFormValues>({
    resolver: zodResolver(commentFormSchema),
    defaultValues: { body: "" },
  });

  const watchedBody = watch("body");

  function addPendingFiles(newFiles: File[]) {
    const remaining = MAX_COMMENT_FILES - pendingFiles.length;
    if (remaining <= 0) {
      setPrompt({ title: t("messages.errorTitle"), text: t("commentForm.errors.tooManyFiles"), variant: "error" });
      return;
    }

    const validFiles: File[] = [];
    for (const file of newFiles) {
      if (validFiles.length + pendingFiles.length >= MAX_COMMENT_FILES) {
        setPrompt({ title: t("messages.errorTitle"), text: t("commentForm.errors.tooManyFiles"), variant: "error" });
        break;
      }
      if (file.size > MAX_COMMENT_FILE_SIZE_MB * 1024 * 1024) {
        setPrompt({ title: t("messages.errorTitle"), text: t("commentForm.errors.fileTooLarge"), variant: "error" });
        continue;
      }
      if (!ALLOWED_COMMENT_MIME_TYPES.includes(file.type)) {
        setPrompt({ title: t("messages.errorTitle"), text: t("commentForm.errors.invalidFileType"), variant: "error" });
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setPendingFiles((prev) => [...prev, ...validFiles]);
    }
  }

  function removePendingFile(index: number) {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function clearPendingFiles() {
    setPendingFiles([]);
  }

  async function handleAddComment(data: CommentFormValues) {
    if (isSubmitting) return;
    if (!data.body.trim() && pendingFiles.length === 0) {
      setPrompt({ title: t("messages.errorTitle"), text: t("commentForm.errors.emptyComment"), variant: "error" });
      return;
    }
    try {
      setIsSubmitting(true);
      await addComment({
        projectId,
        taskId,
        body: data.body,
        files: pendingFiles.length > 0 ? pendingFiles : undefined,
      }).unwrap();
      reset();
      clearPendingFiles();
      setWaitingForRefetch("add");
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.commentError", t),
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  function startEditComment(commentId: number) {
    setEditingId(commentId);
  }

  function cancelEditComment() {
    setEditingId(null);
  }

  async function submitEditComment(
    commentId: number,
    body: string,
    files?: File[],
    removeAttachmentIds?: number[],
  ) {
    if (isEditSubmitting) return;
    try {
      setIsEditSubmitting(true);
      await editComment({ projectId, taskId, commentId, body, files, removeAttachmentIds }).unwrap();
      setWaitingForRefetch("edit");
    } catch (err: unknown) {
      setIsEditSubmitting(false);
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.commentEditError", t),
        variant: "error",
      });
    }
  }

  function requestDeleteComment(commentId: number) {
    setConfirmDeleteId(commentId);
  }

  function cancelDeleteComment() {
    setConfirmDeleteId(null);
  }

  async function confirmDeleteComment() {
    if (confirmDeleteId === null || deletingId !== null) return;
    try {
      setDeletingId(confirmDeleteId);
      await deleteComment({ projectId, taskId, commentId: confirmDeleteId }).unwrap();
      setWaitingForRefetch("delete");
    } catch (err: unknown) {
      setDeletingId(null);
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.commentDeleteError", t),
        variant: "error",
      });
    } finally {
      setConfirmDeleteId(null);
    }
  }

  return {
    register,
    handleSubmit,
    errors,
    watchedBody,
    isAddingComment: isSubmitting || waitingForRefetch === "add",
    isDeletingComment: deletingId !== null,
    deletingId,
    editingId,
    isEditSubmitting,
    confirmDeleteId,
    prompt,
    setPrompt,
    pendingFiles,
    addPendingFiles,
    removePendingFile,
    clearPendingFiles,
    handleAddComment,
    startEditComment,
    cancelEditComment,
    submitEditComment,
    requestDeleteComment,
    confirmDeleteComment,
    cancelDeleteComment,
  };
}
